// Spec 27 PR 2a — SSE consumer for /api/onboarding/turn.
//
// Each turn returns a stream of events (see TurnStreamEvent in
// apps/api/src/lib/gen-ui/action-types.ts). This hook opens the
// stream, parses each event, and dispatches to caller-supplied
// handlers. The caller is responsible for any local-state updates
// it wants to make in response.
//
// PR 2a: hook exists but isn't called yet (no turn endpoint).
// PR 2b: /welcome's dialogue column drives it.

import { useCallback, useEffect, useRef, useState } from "react";

import type { TurnStreamEvent } from "./types";

interface UseTurnStreamOpts {
	/** The current onboarding session id. The stream is keyed off
	 *  this so closing the session cancels any in-flight stream. */
	sessionId: string | null;
	/** Called per-event. The caller decides how each affects local
	 *  state. Receivers should be stable references — this hook
	 *  does not memoize them. */
	onEvent: (event: TurnStreamEvent) => void;
	/** Called when the stream errors out (network, 5xx, parse fail).
	 *  The hook also flips `error` for inspection. */
	onError?: (error: Error) => void;
}

interface TurnStreamHandle {
	/** Fire a turn. Resolves when the stream's `done` event lands.
	 *  Rejects on transport error. */
	send: (input: TurnInputPayload) => Promise<void>;
	/** True while a turn is in flight (between request start and
	 *  done/error). */
	inFlight: boolean;
	error: Error | null;
}

export type TurnInputPayload =
	| { kind: "text"; text: string }
	| {
			kind: "widget";
			action: { widgetId: string; action: string; values?: unknown };
		}
	| { kind: "resume" }
	| { kind: "skip_step"; stepHint?: string };

export function useTurnStream({
	sessionId,
	onEvent,
	onError,
}: UseTurnStreamOpts): TurnStreamHandle {
	const [inFlight, setInFlight] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		// Cancel any in-flight stream when the session changes or the
		// component unmounts. Avoids dangling fetches and double-dispatch.
		return () => {
			abortRef.current?.abort();
		};
	}, [sessionId]);

	const send = useCallback(
		async (input: TurnInputPayload) => {
			if (!sessionId) {
				throw new Error("no_session");
			}
			abortRef.current?.abort();
			const ac = new AbortController();
			abortRef.current = ac;
			setInFlight(true);
			setError(null);

			try {
				const r = await fetch(
					`/api/onboarding/turn?sessionId=${encodeURIComponent(sessionId)}`,
					{
						method: "POST",
						credentials: "include",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ input }),
						signal: ac.signal,
					},
				);
				if (!r.ok || !r.body) {
					throw new Error(`turn:${r.status}`);
				}

				// Parse SSE: lines of `data: <json>\n\n`, terminated by
				// either a `done` event or stream close.
				const reader = r.body.getReader();
				const decoder = new TextDecoder();
				let buffer = "";
				while (true) {
					const { value, done } = await reader.read();
					if (done) break;
					buffer += decoder.decode(value, { stream: true });
					const events = buffer.split("\n\n");
					buffer = events.pop() ?? "";
					for (const raw of events) {
						const line = raw.trim();
						if (!line.startsWith("data:")) continue;
						const json = line.slice("data:".length).trim();
						if (!json) continue;
						try {
							const event = JSON.parse(json) as TurnStreamEvent;
							onEvent(event);
							if (event.kind === "done") return;
						} catch (e) {
							console.warn("[gen-ui] failed to parse turn event:", json);
						}
					}
				}
			} catch (e) {
				if (ac.signal.aborted) return;
				const err = e instanceof Error ? e : new Error(String(e));
				setError(err);
				onError?.(err);
				throw err;
			} finally {
				setInFlight(false);
			}
		},
		[sessionId, onEvent, onError],
	);

	return { send, inFlight, error };
}
